import ApiService from "../../../services/ApiService";
import Logger from "../../../services/Logger";
import { getAdminContentLanguage } from "../../../services/LangPrefs";

export const DRAFT = "DRAFT";
export const READY_TO_REVIEW = "READY_TO_REVIEW";
export const REVIEWED = "REVIEWED";
export const PUBLISHED = "PUBLISHED";
export const DISCARDED = "DISCARDED";
export const DISABLED = "DISABLED";

const AdminCrossBoardService = {
    requestList: async (paginationModel, sort, setRows, setRowCount, keyword, state, selectedGenreId, language, tags, free, difficultyLevel) => {
        let param = {
            page: paginationModel.page,
            pageSize: paginationModel.pageSize,
            sort: sort.map((s) => `${s.field}:${s.sort}`).join(","),
            keyword: keyword ? encodeURIComponent(keyword) : "",
        };

        if (state) {
            param.state = state;
        }

        if (selectedGenreId && selectedGenreId !== "all") {
            param.genreId = selectedGenreId;
        }

        // Include selected language (en/fr) from admin.content.lang so API filters by it - same as grid puzzle
        if (language) {
            param.language = language;
        }

        // Add tags filter if provided (comma-separated string)
        if (tags && Array.isArray(tags) && tags.length > 0) {
            // Convert tags to strings and normalize to lowercase for case-insensitive matching
            const tagStrings = tags.map(tag => {
                let tagString;
                if (typeof tag === 'string') {
                    tagString = tag;
                } else {
                    tagString = tag.tag || String(tag);
                }
                // Normalize to lowercase for case-insensitive filtering
                return tagString.toLowerCase().trim();
            }).filter(tag => tag.length > 0); // Remove empty tags
            param.tags = tagStrings.join(",");
        }

        // Add free filter if provided (true/false/null)
        if (free !== null && free !== undefined) {
            param.free = free === true || free === "true";
        }

        // Add difficulty level filter if provided (EASY/MEDIUM/HARD)
        if (difficultyLevel && difficultyLevel !== "all") {
            param.difficultyLevel = difficultyLevel;
        }

        const result = await ApiService.getData("/v1/puzzle/cross-tpl", param);
        setRows(result.content || []);
        const totalCount = result.page?.totalElements ?? 0;
        if (totalCount === 0 && result.content && result.content.length > 0) {
            Logger.warn("API response missing totalElements. Response structure:", result);
        }
        setRowCount(totalCount);

        return result;
    },

    requestCrossTpl: async (id) => {
        try {
            const result = await ApiService.getData(`/v1/puzzle/cross-tpl/${id}`);
            return result;
        } catch (error) {
            Logger.error(`Error fetching puzzle with ID ${id}:`, error);
            throw error;
        }
    },

    updateCrossTplState: async (id, newState, puzzleType = null, details = null) => {
        try {
            const payload = { state: newState };
            if (puzzleType) {
                payload.type = puzzleType;
            }
            if (details) {
                payload.details = details;
            }
            const result = await ApiService.putData(`/v1/puzzle/cross-tpl/${id}/state`, payload);
            
            // Check if the response indicates failure
            if (result && result.code === "FAILURE") {
                const errorMessage = result.message || "Failed to update puzzle state";
                throw new Error(errorMessage);
            }
            
            return result;
        } catch (error) {
            Logger.error(`Error updating puzzle state for ID ${id}:`, error);
            throw error;
        }
    },

    /**
     * @param {boolean} makeNow - Set publishedAt to current date and time.
     */
    publishCrossTpl: async (
        id,
        publishComment,
        details,
        isFree,
        makeNow = true
    ) => {
        try {
            const payload = {
                comment: publishComment,
                details: details,
                isFree: isFree,
                makeNow: Boolean(makeNow),
            };
            const result = await ApiService.putData(`/v1/puzzle/cross-tpl/${id}/publish`, payload);
            return result;
        } catch (error) {
            Logger.error(`Error updating puzzle state for ID ${id}:`, error);
            throw error;
        }
    },

    setClue: async (word, definition, puzzleId, saveAsHiddenMessage = false) => {
        try {
            const payload = {
                definition: definition
            };

            if (saveAsHiddenMessage) {
                payload.hiddenMessage = word;
            }

            const result = await ApiService.putData(`/v1/puzzle/cross-tpl/${puzzleId}/set-clue`, payload);
            return result;
        } catch (error) {
            Logger.error(`Error setting clue for word "${word}" in puzzle ${puzzleId}:`, error);
            throw error;
        }
    },

    updatePuzzleCoordinates: async (puzzleId, filledCoordinates, newWord) => {
        try {
            let payload = {
                crossTplId: puzzleId,
                updatedCoords: JSON.stringify(filledCoordinates),
            };

            if (newWord) {
                payload.newWord = newWord;
            }

            const result = await ApiService.putData(`/v1/puzzle/cross-tpl/filled_coords`, payload);

            return result;
        } catch (error) {
            Logger.error(`Error updating puzzle coordinates for puzzle ${puzzleId}:`, error);
            throw error;
        }
    },

    markAsDiscarded: async (tplId) => {
        try {
            const result = await ApiService.putData(`/v1/puzzle/cross-tpl/${tplId}/state`, { state: DISCARDED });
            return result;
        } catch (error) {
            Logger.error(`Error marking template as discarded with ID ${tplId}:`, error);
            throw error;
        }
    },

    setFree: async (crossTplId, isFree) => {
        try {
            const result = await ApiService.putData(`/v1/puzzle/cross-tpl/${crossTplId}/set-free/${!!isFree}`);
            return result;
        } catch (error) {
            Logger.error(`Error setting free for crossword template ${crossTplId}:`, error);
            throw error;
        }
    },

    cleanUp: async (crossTpl) => {
        try {
            // Logger.log("crossTpl", crossTpl);
            // Logger.log("coordinates", JSON.parse(crossTpl.coordinates));

            const filledCoordinates = JSON.parse(crossTpl.filledCoordinates);
            const updatedCoordinates = filledCoordinates
                .filter((coord) => coord.text !== null)
                .map(coord => ({ ...coord, text: coord.text.toLowerCase() }));
            // Logger.log("updatedCoordinates", updatedCoordinates);

            const result = await ApiService.putData(`/v1/puzzle/cross-tpl/${crossTpl.id}/cleanUp`, {
                updatedCoordinates: JSON.stringify(updatedCoordinates)
            });
            return result;
        } catch (error) {
            Logger.error(`Error cleaning up puzzle with ID ${crossTpl.id}:`, error);
            throw error;
        }
    },

    /**
     * Rearrange words in the crossword puzzle
     */
    rearrangeWords: async (tplId) => {
        const result = await ApiService.putData(`/v1/puzzle/cross-tpl/${tplId}/rearrange`, {});
        return result;
    },

    updatePuzzleInfo: async (crossTplId, title, isFree, tags = null) => {
        try {
            const payload = {
                title: title,
                isFree: isFree
            };
            if (tags !== null && tags !== undefined && Array.isArray(tags)) {
                payload.tags = tags.map((tag) => String(tag).trim()).filter((tag) => tag.length > 0);
            }
            const result = await ApiService.putData(`/v1/puzzle/cross-tpl/${crossTplId}/info`, payload);
            return result;
        } catch (error) {
            Logger.error(`Error updating puzzle info with ID ${crossTplId}:`, error);
            throw error;
        }
    },


    updateDifficultyLevel: async (tplId, difficultyLevel) => {
        try {
            const result = await ApiService.putData(`/v1/puzzle/cross-tpl/${tplId}/difficulty`, {
                difficultyLevel: difficultyLevel
            });
            return result;
        } catch (error) {
            Logger.error(`Error updating difficulty level for puzzle ${tplId}:`, error);
            throw error;
        }
    },


    /**
     * Generate word network for a puzzle
     * @param {string} tplId - The puzzle template ID
     * @returns {Promise<Object>} Object containing response code
     */
    generateWordNetwork: async (tplId) => {
        try {
            const result = await ApiService.putData(`/v1/puzzle/cross-tpl/${tplId}/gen-word-network`);
            return result;
        } catch (error) {
            Logger.error(`Error generating word network for puzzle ${tplId}:`, error);
            throw error;
        }
    },

    /**
     * Refresh word network for a puzzle
     * @param {string} tplId - The puzzle template ID
     * @returns {Promise<Object>} Object containing response code
     */
    refreshWordNetwork: async (tplId, language) => {
        try {
            const result = await ApiService.putData(`/v1/puzzle/cross-tpl/${tplId}/refresh-word-network?language=${language}`);
            return result;
        } catch (error) {
            Logger.error(`Error refreshing word network for puzzle ${tplId}:`, error);
            throw error;
        }
    },

    /**
     * Refresh difficulty level for a puzzle
     * @param {string} tplId - The puzzle template ID
     * @returns {Promise<Object>} Object containing response code and updated difficulty level
     */
    refreshDifficultyLevel: async (tplId) => {
        try {
            const result = await ApiService.putData(`/v1/puzzle/cross-tpl/${tplId}/refresh-difficulty-level`);
            return result;
        } catch (error) {
            Logger.error(`Error refreshing difficulty level for puzzle ${tplId}:`, error);
            throw error;
        }
    },

    /**
     * Apply title hook for a puzzle
     * @param {string} tplId - The puzzle template ID
     * @returns {Promise<Object>} Object containing response code
     */
    applyTitleHook: async (tplId) => {
        try {
            const result = await ApiService.putData(`/v1/puzzle/cross-tpl/${tplId}/title-hook`);
            return result;
        } catch (error) {
            Logger.error(`Error applying title hook for puzzle ${tplId}:`, error);
            throw error;
        }
    },

    /**
     * Fill bedrock for a puzzle
     * @param {string} tplId - The puzzle template ID
     * @returns {Promise<Object>} Object containing response code
     */
    fillBedrock: async (tplId) => {
        try {
            const result = await ApiService.putData(`/v1/puzzle/cross-tpl/${tplId}/fill-assoc/bedrock`);
            Logger.log("result", result);
            return result;
        } catch (error) {
            Logger.error(`Error filling bedrock for puzzle ${tplId}:`, error);
            throw error;
        }
    },

    /**
     * Get fill-assoc Bedrock candidates (GET). Same shape as grid /fill-assoc/bedrock.
     * @param {string} tplId - The puzzle template ID
     * @returns {Promise<{ wordsFound: number, wordsWithPositions: Array<{ word: string, positions: Array }> }>}
     */
    getFillAssocBedrock: async (tplId) => {
        const result = await ApiService.getData(`/v1/puzzle/cross-tpl/${tplId}/fill-assoc/bedrock`);
        return {
            wordsFound: result?.wordsFound ?? 0,
            wordsWithPositions: Array.isArray(result?.wordsWithPositions) ? result.wordsWithPositions : []
        };
    },

    /**
     * Fill one word from dictionary into an empty slot
     * @param {string} tplId - The puzzle template ID
     * @returns {Promise<Object>} Object containing filledCount, insertedWords, message
     */
    fillPattern: async (tplId) => {
        try {
            const result = await ApiService.putData(`/v1/puzzle/cross-tpl/${tplId}/fill-pattern`);
            return result;
        } catch (error) {
            Logger.error(`Error filling word from dictionary for puzzle ${tplId}:`, error);
            throw error;
        }
    },

    /**
     * Get candidate words for empty pattern slots in the crossword puzzle.
     * Each item includes word, meaning (by part of speech), direction, positions, and info (score, frequency).
     * @param {string} tplId - The puzzle template ID
     * @returns {Promise<Array>} Array of { word, meaning, direction, positions, info }
     */
    getCandidatesPatternTags: async (tplId) => {
        try {
            const result = await ApiService.getData(`/v1/puzzle/cross-tpl/${tplId}/fill-candidates`);
            return Array.isArray(result) ? result : (result?.content || result?.words || []);
        } catch (error) {
            Logger.error(`Error fetching fill pattern for puzzle ${tplId}:`, error);
            throw error;
        }
    },

    /**
     * Match keyword in crossword template - find words matching the keyword pattern
     * @param {string} tplId - The puzzle template ID
     * @param {string|null} keyword - Optional search keyword (trimmed; omit for all)
     * @returns {Promise<{ wordsFound: number, wordsWithPositions: Array<{ word: string, positions: Array<{ row: number, col: number }> }> }>}
     */
    getMatchKeyword: async (tplId, keyword) => {
        const params = {};
        if (keyword != null && String(keyword).trim() !== "") {
            params.keyword = String(keyword).trim();
        }
        const result = await ApiService.getData(`/v1/puzzle/cross-tpl/${tplId}/match-keyword`, params);
        return {
            wordsFound: result?.wordsFound ?? 0,
            wordsWithPositions: Array.isArray(result?.wordsWithPositions) ? result.wordsWithPositions : []
        };
    },

    /**
     * GET final-6: find slots left-to-right and top-to-bottom, max length 6 (4–6 cells).
     * Same response shape: wordsFound, wordsWithPositions.
     */
    getFinal6Candidates: async (tplId) => {
        const result = await ApiService.getData(`/v1/puzzle/cross-tpl/${tplId}/final-6`);
        return {
            wordsFound: result?.wordsFound ?? 0,
            wordsWithPositions: Array.isArray(result?.wordsWithPositions) ? result.wordsWithPositions : []
        };
    },

    /**
     * Batch-publish newest REVIEWED crossword templates for a tag.
     * POST /v1/puzzle/cross-tpl/publish?batchSize=…&language=…&tag=…
     */
    publishReviewed: async (batchSize = 1, language, tag) => {
        try {
            const n = Number(batchSize);
            const clamped = Number.isFinite(n) ? Math.min(1000, Math.max(1, Math.round(n))) : 1;
            const params = new URLSearchParams();
            params.set("batchSize", String(clamped));
            const lang = language != null ? String(language).trim() : "";
            if (lang) params.set("language", lang);
            const tagTrim = tag != null ? String(tag).trim() : "";
            if (tagTrim) params.set("tag", tagTrim);
            const response = await ApiService.postData(
                `/v1/puzzle/cross-tpl/publish?${params.toString()}`,
                {}
            );
            if (response?.code === "FAILURE") {
                return { success: false, error: response.message || "Publish failed", data: null };
            }
            return {
                success: true,
                data: {
                    requestedBatchSize:
                        response?.requestedBatchSize ?? response?.data?.requestedBatchSize ?? clamped,
                    publishedCount: response?.publishedCount ?? response?.data?.publishedCount ?? 0,
                    tplIds: response?.tplIds ?? response?.data?.tplIds ?? [],
                    raw: response,
                },
            };
        } catch (e) {
            Logger.error("AdminCrossBoardService.publishReviewed:", e);
            return { success: false, error: e.message || "Failed to publish crossword puzzles", data: null };
        }
    }
};

export default AdminCrossBoardService;

